import { pingRequest } from './pingRequest';
import { RoundRobinConnection } from './roundRobinConnection';

const STORAGE_PREFIX = 'sp.urlFallback.';
const DEBUG_PING_REQUEST_KEY = 'debugPingRequest';

/** HTTP-статусы, при которых имеет смысл пробовать следующий URL. */
export const defaultIsFallbackError = (status: number): boolean =>
  status === 0 || status === 502 || status === 503 || status === 504;

interface PersistedUrlFallbackState {
  fingerprint: string;
  index: number;
  pingResults?: string[];
  pingResultsFingerprint?: string;
  /**
   * Сколько URL ответили успехом в последнем ping. В pingResults попадают все хосты
   * (успешные — с начала, упавшие — с конца), поэтому «есть ли рабочий результат»
   * определяется именно этим счётчиком, а не длиной массива (см. hasUsablePingSnapshot).
   */
  successfulCount?: number;
  /**
   * Момент (Date.now()) последнего успешного ответа хоста в ping. Обновляется по мере ответов
   * (не по завершении всего цикла), чтобы перебор в запросе мог отреагировать уже на первый
   * быстрый успех, не дожидаясь досчёта медленных/таймаутящих хостов — см. hasFreshUsablePingSince.
   */
  lastSuccessAt?: number;
}

/**
 * Fallback по списку URL.
 * В localStorage под ключом пула хранится единый централизованный snapshot, общий для всех
 * инстансов виджета на origin: текущий индекс round-robin, порядок URL по последнему ping
 * (pingResults) и метаданные результата (successfulCount/lastSuccessAt). Модель —
 * stale-while-revalidate: инстанс сразу работает по последнему snapshot, а фоновый ping лишь
 * обновляет его; см. hasUsablePingSnapshot и pingRequest.shouldWaitForPingRequest.
 */
export class UrlFallback {
  private readonly isFallbackError: (status: number) => boolean;
  private readonly storageKey: string;
  private readonly pingRequestAddress: string;
  private urlsFingerprint = '';
  private appliedPingResultsFingerprint = '';
  // Присваивается в setUrls(), который конструктор вызывает всегда (TS не отслеживает эту косвенность).
  private rr!: RoundRobinConnection;
  private urlsList: string[] = [];

  constructor(
    proxyHosts: string[],
    /** Ключ для сохранения состояния в localStorage (уникален в рамках приложения) */
    storageKey: string,
    /** Путь контрольного запроса (пинга), общий для этого пула proxy-хостов */
    pingRequestAddress: string,
    isFallbackError: (status: number) => boolean,
  ) {
    this.storageKey = storageKey;
    this.pingRequestAddress = pingRequestAddress;
    this.isFallbackError = isFallbackError;
    this.setUrls(proxyHosts);
  }

  setUrls(proxyHosts: string[], runPing: boolean = false): void {
    if (!proxyHosts.length) {
      throw new Error('UrlFallback: список URL пуст');
    }

    const normalizedUrls = proxyHosts.map(normalizeBaseUrl);
    this.urlsList = [...normalizedUrls];
    this.urlsFingerprint = buildUrlsFingerprint(normalizedUrls);
    this.appliedPingResultsFingerprint = '';
    this.rr = new RoundRobinConnection(normalizedUrls);
    this.rr.setAllowedUrls(this.getUrls());

    this.restoreFromStorage(normalizedUrls.length);
    this.persistState();

    if (runPing) {
      void pingRequest(this);
    }
  }

  getUrls(): readonly string[] {
    return this.urlsList;
  }

  /** storageKey, переданный в конструктор, напр. `api`. */
  getStorageKeySuffix(): string {
    return this.storageKey;
  }

  /** Путь контрольного запроса (пинга) для этого пула хостов. */
  getPingRequestAddress(): string {
    return this.pingRequestAddress;
  }

  /**
   * Есть ли пригодный к использованию результат ping для текущего набора URL: сохранён
   * snapshot того же fingerprint, в котором хотя бы один хост ответил успехом. Если да —
   * потребитель (запрос/плашка) работает по нему сразу, не дожидаясь текущего фонового ping.
   */
  hasUsablePingSnapshot(): boolean {
    const state = loadRawPersistedState(this.storageKey);
    return (
      !!state &&
      state.fingerprint === this.urlsFingerprint &&
      (state.successfulCount ?? 0) >= 1
    );
  }

  /**
   * Момент последнего успешного ответа хоста в ping для текущего набора URL (0, если успеха
   * ещё не было). Снимается на старте fallback-цикла как отметка, с которой сравнивают приход
   * свежего успеха — см. hasFreshUsablePingSince.
   */
  getLastPingSuccessAt(): number {
    const state = loadRawPersistedState(this.storageKey);
    return state?.fingerprint === this.urlsFingerprint
      ? (state.lastSuccessAt ?? 0)
      : 0;
  }

  /**
   * Пришёл ли за время fallback-цикла свежий успех пинга: пригодный snapshot (хотя бы один
   * успешный хост), у которого lastSuccessAt новее переданной отметки. Реагирует на первый же
   * быстрый успех фонового ping (а не на завершение всего цикла). Индекс round-robin и порядок
   * URL шарятся между фоновым ping и перебором в запросе, поэтому запрос, наткнувшийся на
   * упавшие хосты, по этому признаку понимает, что ping уже переизбрал быстрейший хост, и может
   * перезапустить перебор с нуля вместо доигрывания устаревшего порядка.
   */
  hasFreshUsablePingSince(stamp: number): boolean {
    const state = loadRawPersistedState(this.storageKey);
    return (
      !!state &&
      state.fingerprint === this.urlsFingerprint &&
      (state.successfulCount ?? 0) >= 1 &&
      (state.lastSuccessAt ?? 0) > stamp
    );
  }

  /**
   * Финализация ping-цикла: публикует итоговое число успешных хостов и момент завершения,
   * сохраняя уже записанные (по мере ответов) pingResults и индекс. successfulCount === 0
   * (все хосты упали) снимает пригодность snapshot — потребители уйдут на исходный порядок
   * и штатный fallback до следующего успешного ping.
   */
  markPingCompleted(successfulCount: number): void {
    if (!this.rr) {
      return;
    }
    // index/pingResults/lastSuccessAt не передаём — они уже записаны по мере ответов;
    // финализируем только авторитетное число успешных (в т.ч. 0, если все хосты упали),
    // сохранив выставленные инкрементальной записью позицию, порядок и отметку успеха.
    savePersistedState(this.storageKey, this.urlsFingerprint, undefined, undefined, {
      successfulCount,
    });
  }

  savePingResult(
    index: number,
    baseUrl: string,
    isSuccess: boolean = false,
  ): void {
    logPingRequestStorage('savePingResult-start', {
      storageKey: this.storageKey,
      index,
      baseUrl,
      isSuccess,
    });
    const pingResults = this.getPingResults();
    logPingRequestStorage('savePingResult-current-results', {
      storageKey: this.storageKey,
      index,
      pingResultsBefore: pingResults,
      pingResultsBeforeView: formatArrayForLog(pingResults),
    });
    pingResults[index] = baseUrl;
    logPingRequestStorage('savePingResult-after-assign', {
      storageKey: this.storageKey,
      index,
      baseUrl,
      pingResultsAfter: pingResults,
      pingResultsAfterView: formatArrayForLog(pingResults),
    });
    // Успехи приходят с индексами 0,1,2… в порядке ответа, поэтому текущее число успешных =
    // index + 1. Пишем его сразу — чтобы уже первый ответивший хост делал snapshot пригодным
    // (hasUsablePingSnapshot) и обновление не «проваливало» пригодность между двумя ping.
    // На провалившемся хосте счётчик не трогаем: сохраняется значение прошлого цикла.
    this.savePingResults(
      pingResults,
      isSuccess,
      isSuccess
        ? { successfulCount: index + 1, lastSuccessAt: Date.now() }
        : undefined,
    );
  }

  /**
   * Проверяет, стоит ли при данном HTTP-статусе пробовать следующий URL.
   * Ошибки уровня API (401, 404 и т.п.) фолбэком не лечатся.
   */
  shouldFallback(httpStatus: number): boolean {
    return this.isFallbackError(httpStatus);
  }

  tryGetCurrentAvailableUrl(
    excludedIndexes: ReadonlySet<number> = new Set<number>(),
  ): { url: string; index: number } | null {
    return this.pickNearestAvailableUrl(excludedIndexes);
  }

  /**
   * Ближайший доступный URL: с текущей позиции RR, пропуская индексы,
   * которые уже упали в рамках текущего fallback-цикла.
   */
  tryGetNearestAvailableUrl(
    excludedIndexes: ReadonlySet<number> = new Set<number>(),
  ): { url: string; index: number } | null {
    return this.pickNearestAvailableUrl(excludedIndexes);
  }

  private pickNearestAvailableUrl(
    excludedIndexes: ReadonlySet<number>,
  ): { url: string; index: number } | null {
    this.restoreFromStorage(this.getUrls().length);
    const rr = this.rr.clone();

    if (excludedIndexes.size >= rr.getUrls().length) {
      return null;
    }

    const startIndex = rr.getIndex();
    while (excludedIndexes.has(rr.getIndex())) {
      rr.advance();
      if (rr.getIndex() === startIndex) {
        return null;
      }
    }

    this.rr.setIndex(rr.getIndex());
    this.persistState();
    return { url: rr.current(), index: rr.getIndex() };
  }

  checkAndRecordResult(httpStatus: number, plannedIndex: number): boolean {
    if (this.shouldFallback(httpStatus)) {
      this.advanceFrom(plannedIndex);
      return false;
    }

    this.persistState();
    return true;
  }

  /**
   * Compare-and-swap: продвигаем shared round-robin только если он всё ещё
   * стоит там же, где был на момент старта этого запроса. Если конкурентный
   * запрос уже продвинул индекс дальше — не откатываем его назад чужим
   * устаревшим plannedIndex, просто no-op.
   */
  private advanceFrom(index: number): void {
    if (this.rr.getIndex() === index) {
      this.rr.advance();
    }
    this.persistState();
  }

  private restoreFromStorage(urlCount: number): void {
    const restored = loadPersistedState(
      this.storageKey,
      this.urlsFingerprint,
      urlCount,
    );
    if (!restored) {
      return;
    }

    if (
      restored.pingResults.length &&
      restored.pingResultsFingerprint !== this.appliedPingResultsFingerprint
    ) {
      this.rr.setUrls(restored.pingResults);
      this.appliedPingResultsFingerprint = restored.pingResultsFingerprint;
    }

    const restoredUrlCount = this.rr.getUrls().length;
    const index =
      restored.index >= 0 && restored.index < restoredUrlCount
        ? restored.index
        : 0;
    this.rr.setIndex(index);
  }

  private getPingResults(): string[] {
    const state = loadRawPersistedState(this.storageKey);
    if (state?.fingerprint !== this.urlsFingerprint) {
      logPingRequestStorage('getPingResults-fingerprint-mismatch', {
        storageKey: this.storageKey,
        expectedFingerprint: this.urlsFingerprint,
        actualFingerprint: state?.fingerprint ?? null,
        hasState: !!state,
      });
      return [];
    }

    const pingResults = Array.isArray(state.pingResults)
      ? state.pingResults.map((url) =>
          isNotEmptyString(url) ? normalizeBaseUrl(url) : '',
        )
      : [];
    logPingRequestStorage('getPingResults-success', {
      storageKey: this.storageKey,
      statePingResults: state.pingResults ?? null,
      statePingResultsView: formatArrayForLog(state.pingResults),
      pingResults,
      pingResultsView: formatArrayForLog(pingResults),
      result: pingResults,
      resultView: formatArrayForLog(pingResults),
      resultLength: pingResults.length,
    });
    return pingResults;
  }

  private savePingResults(
    pingResults: string[],
    shouldUpdateIndex: boolean = false,
    meta?: { successfulCount?: number; lastSuccessAt?: number },
  ): void {
    if (!this.rr) {
      logPingRequestStorage('savePingResults-skip', {
        hasRoundRobin: !!this.rr,
        shouldUpdateIndex,
        pingResults,
      });
      return;
    }

    const nextIndex = shouldUpdateIndex ? 0 : this.rr.getIndex();
    logPingRequestStorage('savePingResults-start', {
      storageKey: this.storageKey,
      shouldUpdateIndex,
      nextIndex,
      meta,
      pingResults,
      pingResultsView: formatArrayForLog(pingResults),
    });
    savePersistedState(
      this.storageKey,
      this.urlsFingerprint,
      nextIndex,
      pingResults,
      meta,
    );
  }

  private persistState(): void {
    if (!this.rr) {
      return;
    }

    savePersistedState(
      this.storageKey,
      this.urlsFingerprint,
      this.rr.getIndex(),
    );
  }
}

function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function buildUrlsFingerprint(urls: readonly string[]): string {
  return urls.join('|');
}

function loadPersistedState(
  storageKey: string,
  fingerprint: string,
  urlCount: number,
): {
  index: number;
  pingResults: string[];
  pingResultsFingerprint: string;
} | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as PersistedUrlFallbackState;
    if (parsed.fingerprint !== fingerprint) {
      return null;
    }

    const index =
      parsed.index >= 0 && parsed.index < urlCount ? parsed.index : 0;

    const pingResults = Array.isArray(parsed.pingResults)
      ? parsed.pingResults.filter(isNotEmptyString).map(normalizeBaseUrl)
      : [];
    const pingResultsFingerprint =
      parsed.pingResultsFingerprint ?? buildUrlsFingerprint(pingResults);

    return { index, pingResults, pingResultsFingerprint };
  } catch {
    return null;
  }
}

function isNotEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Публикует состояние пула в localStorage. pingResults и meta (successfulCount/lastSuccessAt)
 * опциональны и независимы: не переданное поле сохраняется из предыдущего snapshot того же
 * fingerprint. За счёт этого инкрементальная запись pingResults не сбрасывает метаданные
 * результата, а финализация (markPingCompleted) не трогает уже записанный порядок URL.
 */
function savePersistedState(
  storageKey: string,
  fingerprint: string,
  index: number | undefined,
  pingResults?: string[],
  meta?: { successfulCount?: number; lastSuccessAt?: number },
): void {
  try {
    const fullStorageKey = STORAGE_PREFIX + storageKey;
    logPingRequestStorage('savePersistedState-start', {
      storageKey,
      fullStorageKey,
      fingerprint,
      index,
      pingResults,
      pingResultsView: formatArrayForLog(pingResults),
      hasIncomingPingResults: !!pingResults,
    });
    const previousState = loadRawPersistedState(storageKey);
    logPingRequestStorage('savePersistedState-previous-state', {
      storageKey,
      previousState,
      previousStateView: formatPersistedStateForLog(previousState),
      previousFingerprintMatches: previousState?.fingerprint === fingerprint,
    });
    const normalizedPingResults = pingResults?.map((url) =>
      isNotEmptyString(url) ? normalizeBaseUrl(url) : '',
    );
    const previousMatches = previousState?.fingerprint === fingerprint;
    const state: PersistedUrlFallbackState = {
      fingerprint,
      // index не передан (напр. markPingCompleted) — сохраняем уже записанный, чтобы
      // финализация не перетирала позицию, выставленную инкрементальной записью результатов.
      index: index ?? (previousMatches ? (previousState?.index ?? 0) : 0),
      pingResults:
        normalizedPingResults ??
        (previousMatches ? previousState?.pingResults : undefined),
      pingResultsFingerprint: normalizedPingResults
        ? buildUrlsFingerprint(normalizedPingResults)
        : previousMatches
          ? previousState?.pingResultsFingerprint
          : undefined,
      successfulCount:
        meta?.successfulCount ??
        (previousMatches ? previousState?.successfulCount : undefined),
      lastSuccessAt:
        meta?.lastSuccessAt ??
        (previousMatches ? previousState?.lastSuccessAt : undefined),
    };
    const serializedState = JSON.stringify(state);
    logPingRequestStorage('savePersistedState-before-setItem', {
      storageKey,
      fullStorageKey,
      normalizedPingResults,
      normalizedPingResultsView: formatArrayForLog(normalizedPingResults),
      state,
      stateView: formatPersistedStateForLog(state),
      serializedLength: serializedState.length,
    });
    localStorage.setItem(fullStorageKey, serializedState);
    logPingRequestStorage('savePersistedState-success', {
      storageKey,
      fullStorageKey,
      state,
      stateView: formatPersistedStateForLog(state),
    });
  } catch (error) {
    logPingRequestStorage('savePersistedState-error', {
      storageKey,
      fingerprint,
      index,
      pingResults,
      pingResultsView: formatArrayForLog(pingResults),
      error: serializeError(error),
    });
    // localStorage недоступен (private mode, quota и т.п.)
  }
}

function loadRawPersistedState(
  storageKey: string,
): PersistedUrlFallbackState | null {
  try {
    const fullStorageKey = STORAGE_PREFIX + storageKey;
    const raw = localStorage.getItem(fullStorageKey);
    logPingRequestStorage('loadRawPersistedState-read', {
      storageKey,
      fullStorageKey,
      hasRaw: !!raw,
      rawLength: raw?.length ?? 0,
    });
    return raw ? (JSON.parse(raw) as PersistedUrlFallbackState) : null;
  } catch (error) {
    logPingRequestStorage('loadRawPersistedState-error', {
      storageKey,
      error: serializeError(error),
    });
    return null;
  }
}

function isPingRequestDebugEnabled(): boolean {
  try {
    return localStorage.getItem(DEBUG_PING_REQUEST_KEY) === '1';
  } catch {
    return false;
  }
}

function logPingRequestStorage(
  event: string,
  payload: Record<string, unknown>,
): void {
  if (!isPingRequestDebugEnabled()) {
    return;
  }

  console.info('[PingRequest]', `storage:${event}`, payload);
  console.info(
    '[PingRequestPlaneJson]',
    stringifyPingRequestLog({ event: `storage:${event}`, payload }),
  );
}

function stringifyPingRequestLog(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return JSON.stringify({
      stringifyError: error instanceof Error ? error.message : String(error),
    });
  }
}

function formatArrayForLog(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.map((item, index) => `${index}:${String(item)}`);
}

function formatPersistedStateForLog(
  state: PersistedUrlFallbackState | null | undefined,
): Record<string, unknown> | null {
  if (!state) {
    return null;
  }

  return {
    fingerprint: state.fingerprint,
    index: state.index,
    pingResultsView: formatArrayForLog(state.pingResults),
    pingResultsFingerprint: state.pingResultsFingerprint,
  };
}

function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  return { value: error };
}
